/* jshint esversion: 6 */

/**
 * RichTextEditorService is a service class that manages the configuration and initialization of a Rich Text Editor using CKEditor.
 * It provides methods to set up the editor, manage plugins, toolbars, and other configurations.
 */
class RichTextEditorService {
    /**
     * Constructor for RichTextEditorService.
     * @param {Object} $rootScope - The root scope object from AngularJS.
     * @param {Object} $injector - The injector service from AngularJS.
     */
    constructor($rootScope, $injector) {
        this._logIdentifier = 'RichTextEditorService';
        this.tag = "[" + this._logIdentifier + "]";
        this.$lastError = '';
        this.$lastMessage = '';
        this.$bridges = $injector.get('$bridges');
        this.$logger = this.$bridges.logger();
        this._inactivityTimeoutService = null;
        this._taskInfoService = $injector.get('TaskInfoService');

        if (typeof CKEDITOR === 'undefined' || !CKEDITOR) {
            this.$lastError = "CKEDITOR is missing. Cannot find an instance";
            throw this.$lastError;
        }

        this.OBJECT_TYPE = {
            TEXT: 'text',
            RESPONSE: 'response-textbox',
            MCQ: 'textMCQ'
        };

        this.DEFAULT_BASEPATH = '/';
        this.SPELLCHECK_PATH = '/ckeditorPlugins/typoSpellcheck/';
        this.SPELLCHECK_DICTIONARIES_PATH = '/ckeditorPlugins/typoSpellcheck/typo/dictionaries';
        this.SPELLCHECK_FILENAME = 'plugin.js';
        this.$editor = null;
        this.$basePath = this.DEFAULT_BASEPATH;
        this.$rootScope = $rootScope;

        CKEDITOR.plugins.addExternal('dialogui', '/ckeditorPlugins/dialogui/', 'plugin.js');
        CKEDITOR.plugins.addExternal('wsc', '/ckeditorPlugins/wsc/', 'plugin.js');
        CKEDITOR.plugins.addExternal('tooltip', '/ckeditorPlugins/tooltip/', 'plugin.js');
        CKEDITOR.plugins.addExternal('wiriseditor', '/ckeditorPlugins/wiriseditor/', 'plugin.js');
        CKEDITOR.plugins.addExternal('panel', '/ckeditorPlugins/panel/', 'plugin.js');
        CKEDITOR.plugins.addExternal('specialcharsetting', '/ckeditorPlugins/specialcharsetting/', 'plugin.js');
        CKEDITOR.plugins.addExternal('wordcount', '/ckeditorPlugins/wordcount/', 'plugin.js');
        CKEDITOR.plugins.addExternal('floatpanel', '/ckeditorPlugins/floatpanel/', 'plugin.js');
        CKEDITOR.plugins.addExternal('strinsert', '/ckeditorPlugins/strinsert/', 'plugin.js');
        CKEDITOR.plugins.addExternal('mathbar', '/ckeditorPlugins/mathbar/', 'plugin.js');
        CKEDITOR.plugins.addExternal('button', '/ckeditorPlugins/button/', 'plugin.js');
        CKEDITOR.plugins.addExternal('lineutils', '/ckeditorPlugins/lineutils/', 'plugin.js');
        CKEDITOR.plugins.addExternal('imagefromcalc', '/ckeditorPlugins/imagefromcalc/', 'plugin.js');
        CKEDITOR.plugins.addExternal('stylescombo', '/ckeditorPlugins/stylescombo/', 'plugin.js');
        CKEDITOR.plugins.addExternal('listblock', '/ckeditorPlugins/listblock/', 'plugin.js');
        CKEDITOR.plugins.addExternal('richcombo', '/ckeditorPlugins/richcombo/', 'plugin.js');
        CKEDITOR.plugins.addExternal('confighelper', '/ckeditorPlugins/confighelper/', 'plugin.js');
        CKEDITOR.plugins.addExternal('typoSpellcheck', '/ckeditorPlugins/typoSpellcheck/', 'plugin.js');


    }

    /**
     * Initializes the editor configuration based on the object type, hidebar flag, and language.
     * @param {string} objectType - The type of object for which the editor is being initialized.
     * @param {boolean} hidebar - Flag to hide the toolbar.
     * @param {string} lang - The language for the editor.
     * @returns {Object} - The configuration object for the editor.
     */
    init(objectType, hidebar, lang) {
        this.$logger.info("Initialising for object type:", objectType);

        if (!objectType || typeof objectType === "undefined" || !this._isValueInObject(this.OBJECT_TYPE, objectType)) {
            this.$lastError = "Init - Given ObjectType [" + objectType + "] not found!\n" +
                "Valid options are :" +
                this.OBJECT_TYPE.TEXT + ", " +
                this.OBJECT_TYPE.RESPONSE + ", " +
                this.OBJECT_TYPE.MCQ;
            this.$logger.warn(this.$lastError);
            throw this.$lastError;
        }

        hidebar = hidebar ? Boolean(hidebar) : false;

        const DEFAULT_SKIN_THEME = "moono",
            DEFAULT_LANGUAGE = 'en',
            DEFAULT_LANG_DIRECTION = 'ltr',
            DEFAULT_STYLES_FILEPATH = '/styles/richtexteditor.css',
            SMILEY_PATH = '/scripts/vendors/ckeditor/plugins/smiley/images/',
            SPECIAL_CHARACTERS = ['&#x3b1;', '&#x3b2;', '&#x3b3;', '&#x3bb;', '&#x3bc;', '&#x3c1;', '&#x3a9;', '&#x3c0;', '&#x2211;', '&#x2206;', '&#x3b8;'],
            FORMAT_TAGS = 'p;h1;h2;h3;pre';

        const BUTTONS = [
            'SelectAll',
            'Bold',
            'Italic',
            'NumberedList',
            'BulletedList',
            'Indent',
            'Outdent',
            'Cut',
            'Copy',
            'Paste',
            'Undo',
            'Redo',
            'Anchor',
            'Underline',
            'Strike',
            'Subscript',
            'Superscript'
        ];

        const DEFAULT_PLUGINS = [
            'autogrow',
            'confighelper',
            'stylescombo',
            'specialchar',
            'wiriseditor',
            'widget',
            'dialog',
            'iframedialog'
        ];

        const IB_TOOLBAR = [
            ['Bold', 'Italic', '-', 'Undo', 'Redo'],
            ['Underline', 'Subscript', 'Superscript'],
            ['NumberedList', 'BulletedList'],
            ['SpecialChar', 'WirisEditor']
        ];

        let _removeButtons = '',
            _toolbar = [],
            _plugins = null;

        if (hidebar) {
            _removeButtons = BUTTONS.join(',');
        } else {
            _toolbar = IB_TOOLBAR;
        }

        const mainRtCSSFile = lang === 'ur' ? DEFAULT_STYLES_FILEPATH.replace('.css', '-urdu.css') : DEFAULT_STYLES_FILEPATH + "?lang=" + lang;

        this.$config = {
            'versionCheck': false,
            'objectType': objectType,
            'allowedContent': true,
            'skin': DEFAULT_SKIN_THEME,
            'defaultLanguage': DEFAULT_LANGUAGE,
            'language': DEFAULT_LANGUAGE,
            'contentsLangDirection': DEFAULT_LANG_DIRECTION,
            'contentsCss': [mainRtCSSFile],
            'stylesSet': [],
            'originalHeight': 120,
            'autoGrow_onStartup': true,
            'autoGrow_maxHeight': 1000,
            'resize_enabled': false,
            'resize_dir': 'vertical',
            'resize_maxWidth': 1000,
            'resize_maxHeight': 1000,
            'resize_minWidth': 300,
            'resize_minHeight': 120,
            'removeDialogTabs': 'image:advanced;link:advanced',
            'startupFocus': false,
            'toolbar': _toolbar,
            'removeButtons': _removeButtons,
            'extraPlugins': DEFAULT_PLUGINS.join(','),
            'removePlugins': '',
            '_removePlugins': {},
            'specialChars': SPECIAL_CHARACTERS,
            'introductionPage': false,
            'inlineEditor': false,
            'smiley_path': SMILEY_PATH,
            'format_tags': FORMAT_TAGS,
            'title': false
        };

        return this.$config;
    }

    /**
     * Initializes the language settings for the editor.
     * @param {boolean} isCMS - Flag indicating if the editor is used in a CMS context.
     * @param {boolean} isPreviewMode - Flag indicating if the editor is in preview mode.
     * @param {boolean} isJournal - Flag indicating if the editor is used in a journal context.
     */
    initLanguage(isCMS, isPreviewMode, isJournal) {
        const taskInfo = this._taskInfoService.getTaskInfo();

        this.set('defaultLanguage', this.$rootScope.i18n.languageCode);
        this.set('language', this.$rootScope.i18n.languageCode);

        if (!isCMS || isPreviewMode) {
            if (taskInfo.isSystemLangSelectable()) {
                this.set('defaultLanguage', taskInfo.getLanguageCode());
                this.set('language', taskInfo.getLanguageCode());
            }
        }

        if (angular.isFunction(taskInfo.getInputLanguage) && angular.isObject(taskInfo.getInputLanguage()) && taskInfo.getInputLanguage().writingDirection) {
            this.set('contentsLangDirection', isJournal ? "ltr" : taskInfo.getInputLanguage().writingDirection);
        }
    }

    /**
     * Retrieves the current configuration of the editor.
     * @returns {Object} - The configuration object.
     */
    getConfig() {
        return Object.assign({}, this.$config);
    }

    /**
     * Creates and returns a CKEditor instance.
     * @param {HTMLElement} domElement - The DOM element to which the editor will be attached.
     * @param {Object} config - The configuration object for the editor.
     * @returns {Object} - The CKEditor instance.
     */
    getEditor(domElement, config) {
        if (typeof domElement === 'undefined' || !domElement) {
            this.$lastError = "Cannot create a CKEditor instance without a dom element. \nDomElem:" + domElement;
            this.$logger.warn(this.$lastError);
            throw this.$lastError;
        }

        if (typeof config !== 'undefined') {
            this.$logger.info(this.tag, 'Using received config', config);
        } else {
            config = this.$config;
            this.$logger.info(this.tag, 'Using DEFAULT config', config);
        }

        if (config.inlineEditor === true) {
            config.fillEmptyBlocks = false;
            CKEDITOR.disableAutoInline = true;
            this.$editor = CKEDITOR.inline(domElement, config);
        } else {
            this.$editor = CKEDITOR.replace(domElement, config);
        }

        return this.$editor;
    }

    /**
     * Retrieves the last error that occurred.
     * @returns {string} - The last error message.
     */
    getLastError() {
        return this.$lastError;
    }

    /**
     * Sets the base path for the editor.
     * @param {string} path - The base path to be set.
     * @returns {string} - The base path that was set.
     */
    setBasePath(path) {
        if (!path || typeof path !== "string") {
            this.$lastError = "Path is missing. Using default path: " + this.$basePath;
            this.$logger.warn(this.$lastError);
            path = this.DEFAULT_BASEPATH;
        }
        this.$basePath = path;
        return this.$basePath;
    }

    /**
     * Retrieves the current base path.
     * @returns {string} - The current base path.
     */
    getBasePath() {
        return this.$basePath;
    }

    /**
     * Sets the configuration object for the editor.
     * @param {Object} obj - The configuration object to be set.
     * @returns {Object} - The configuration object that was set.
     */
    setConfig(obj) {
        if (!angular.isObject(obj) || angular.isObjectEmpty(obj)) {
            this.$lastError = "Cannot set configuration with the given object";
            this.$logger.warn(this.$lastError);
            throw this.$lastError;
        }
        this.$config = obj;
        return this.$config;
    }

    /**
     * Retrieves a specific attribute from the configuration object.
     * @param {string} attribute - The attribute name to retrieve.
     * @returns {*} - The value of the attribute.
     */
    get(attribute) {
        if (!attribute || typeof attribute !== "string") {
            this.$lastError = "Attribute name is missing";
            this.$logger.warn(this.$lastError);
            throw this.$lastError;
        }
        if (!this.$config.hasOwnProperty(attribute)) {
            this.$lastError = "Attribute " + attribute + " was not found";
            this.$logger.warn(this.$lastError);
            throw this.$lastError;
        }
        return this.$config[attribute];
    }

    /**
     * Sets a specific attribute in the configuration object.
     * @param {string} attr - The attribute name to set.
     * @param {*} value - The value to be set for the attribute.
     * @returns {Object} - The updated configuration object.
     */
    set(attr, value) {
        if (!attr || typeof attr !== "string") {
            this.$lastError = "Attribute name must be a String, got " + typeof attr;
            this.$logger.error(this.$lastError);
            throw this.$lastError;
        }
        this.$config[attr] = value;
        return this.$config;
    }

    /**
     * Retrieves the list of extra plugins.
     * @returns {string} - The list of extra plugins.
     */
    getPluginList() {
        return this.$config.extraPlugins;
    }

    /**
     * Sets the removeDialogTabs configuration.
     * @param {string} target - The target for the dialog tabs.
     * @param {string} type - The type of the dialog tabs.
     * @returns {string} - The updated removeDialogTabs configuration.
     */
    setRemoveDialogTabs(target, type) {
        if (!target || typeof target !== "string" || !type || typeof type !== "string") {
            this.$lastError = "two parameters were expected: target:string, type:string." +
                "\nGot: 'target:" + typeof target + "' and 'type:" + typeof type + "'";
            throw this.$lastError;
        }

        if (typeof this.$config.removeDialogTabs === 'undefined') {
            this.$config.removeDialogTabs = target + ':' + type;
        } else {
            this.$config.removeDialogTabs += ";" + target + ":" + type;
        }
        return this.$config.removeDialogTabs;
    }

    /**
     * Adds a plugin to the editor configuration.
     * @param {string} str - The name of the plugin to add.
     * @returns {string} - The updated list of extra plugins.
     */
    addPlugin(str) {
        if (!str || typeof str !== "string") {
            this.$lastError = "String expected for plugin name, got " + typeof str;
            this.$logger.warn(this.$lastError);
            throw this.$lastError;
        }

        let _plugins = this.$config.extraPlugins.split(",");

        if (_plugins.indexOf(str) === -1) {
            _plugins.push(str);
            this.$config.extraPlugins = _plugins.join(",");
        }

        if ('_removePlugins' in this.$config) {
            delete this.$config._removePlugins[str];
        }

        return this.$config.extraPlugins;
    }

    /**
     * Removes a plugin from the editor configuration.
     * @param {string} str - The name of the plugin to remove.
     * @returns {string} - The updated list of extra plugins.
     */
    removePlugin(str) {
        if (!str || typeof str !== "string") {
            this.$lastError = "String expected for plugin name, got " + typeof str;
            this.$logger.warn(this.$lastError);
            throw this.$lastError;
        }

        this.$config.extraPlugins = this._removeFromArray(str, this.$config.extraPlugins.split(",")).join(",");
        this.$config._removePlugins[str] = 1;

        this.updateRemovePlugins();

        return this.$config.extraPlugins;
    }

    /**
     * Updates the removePlugins configuration based on the _removePlugins object.
     */
    updateRemovePlugins() {
        let removePluginsArr = [];
        for (let pluginName in this.$config._removePlugins) {
            if (this.$config._removePlugins.hasOwnProperty(pluginName)) {
                removePluginsArr.push(pluginName);
            }
        }
        this.$config.removePlugins = removePluginsArr.join(',');
    }

    /**
     * Adds a toolbar element or group to the editor configuration.
     * @param {string|Array} str - The toolbar element or group to add.
     * @param {number} position - The position at which to add the toolbar element or group.
     */
    addToolbar(str, position) {
        if (angular.isArray(str)) {
            let check = false;
            let tools = [];
            for (let i in str) {
                if (this._isInToolbar(str[i], this.$config)) {
                    check = true;
                    tools.push(str[i]);
                }
            }
            if (check) {
                this.$lastError = this.tag + " Cannot add given toolbar, 1 or more element are already defined\n" +
                    this.tag + " Repeated elements: " + JSON.stringify(tools);
                console.warn(this.$lastError);
                return;
            }
            this.$config.toolbar.push(str);
            return true;
        } else if (!str || typeof str !== "string") {
            this.$lastError = this.tag + " String expected, got " + typeof str;
            this.$logger.warn(this.$lastError);
            throw this.$lastError;
        }

        if (!this._isInToolbar(str, this.$config)) {
            position = position && typeof position === "number" ? position : this.$config.toolbar.length + 1;

            if (position > this.$config.toolbar.length) {
                this.$config.toolbar.push([str]);
            } else {
                this.$config.toolbar[position].push(str);
            }
        }
    }

    removeToolbar(str) {
        if (!str || typeof str !== "string") {
            this.$lastError = "String expected, got " + typeof str;
            throw this.$lastError;
        }
        for (let i = this.$config.toolbar.length - 1; i >= 0; i--) {
            if (angular.isArray(this.$config.toolbar[i])) {
                let _currBar = this.$config.toolbar[i];
                for (let j = _currBar.length - 1; j >= 0; j--) {
                    if (_currBar[j] === str) {
                        _currBar.splice(j, 1);
                    }
                }
            } else if (typeof this.$config.toolbar[i] === "string") {
                if (this.$config.toolbar[i] === str) {
                    this.$config.toolbar.splice(i, 1);
                }
            }
        }
    }

    enableToolTip(enable) {
        enable = typeof enable !== "undefined" ? Boolean(enable) : true;

        if (enable) {
            if (this.$config.extraPlugins.indexOf('tooltip') === -1) {
                this.addPlugin('tooltip');
                this.addToolbar('tooltip');
            }
        } else {
            this.removePlugin('tooltip');
            this.removeToolbar('tooltip');
        }
    }

    enableSpellcheck(editorId, enable) {
        console.log("Spellcheck temporarily disabled");
        return;
        if (!angular.isDefined(editorId) || typeof editorId !== "string") {
            this.$lastError = "string editorId expected, got " + typeof editorId;
            this.$logger.error(this.$lastError);
            throw this.$lastError;
        }

        enable = typeof enable !== "undefined" ? Boolean(enable) : false;

        const pluginname = 'typoSpellcheck';

        if (enable) {
            /*CKEDITOR.plugins.addExternal(
                pluginname,
                this.DEFAULT_BASEPATH + this.SPELLCHECK_PATH,
                this.SPELLCHECK_FILENAME
            );*/

            const _typoSpellcheckConf = {};
            _typoSpellcheckConf[editorId] = {
                dictionaryPath: this.DEFAULT_BASEPATH + this.SPELLCHECK_DICTIONARIES_PATH,
                enabled: false,
                cssClass: 'spellchecker-red'
            };

            this.set(pluginname, _typoSpellcheckConf);
            this.addPlugin(pluginname);
        } else {
            this.removePlugin(pluginname);
            this.set(pluginname, null);
        }
    }

    enableWordCount(enable) {
        enable = typeof enable !== "undefined" ? Boolean(enable) : true;

        if (enable) {
            this.addPlugin('wordcount');
        } else {
            this.removePlugin('wordcount');
        }
    }

    enableImageFromCalc(enable) {
        enable = typeof enable !== "undefined" ? Boolean(enable) : false;

        if (enable) {
            this.addPlugin('imagefromcalc');
            if (!this._isInToolbar('imagefromcalc', this.$config)) {
                this.addToolbar('imagefromcalc');
            }
        } else {
            this.removePlugin('imagefromcalc');
        }
    }

    enablePlaceholder(enable) {
        enable = typeof enable !== "undefined" ? Boolean(enable) : false;

        if (enable) {
            this.addPlugin('placeholder');
        } else {
            this.removePlugin('placeholder');
        }
    }

    enableStringInsert(enable) {
        enable = typeof enable !== "undefined" ? Boolean(enable) : false;

        if (enable) {
            this.addPlugin('strinsert');
        } else {
            this.removePlugin('strinsert');
        }
    }

    enableIntroductionPage(enable) {
        enable = typeof enable !== "undefined" ? Boolean(enable) : false;

        if (enable) {
            this.addPlugin('placeholder');
            this.addPlugin('strinsert');
            if (!this._isInToolbar('tooltip', this.$config)) {
                this.addToolbar('tooltip');
            }
            this.$config.introductionPage = true;
        } else {
            this.removePlugin('placeholder');
            this.removePlugin('strinsert');
            this.$config.introductionPage = false;
        }
    }

    addStyleSet(obj) {
        if (!obj) {
            this.$lastError = "Cannot set editor styles. Attributes are missing!";
            throw this.$lastError;
        }

        if (!obj.hasOwnProperty('name') || !obj.hasOwnProperty('element') || !obj.hasOwnProperty('attributes')) {
            this.$lastError = "Cannot set editor styles.\nI was expecting an object like obj {name: 'string', element: '<domelement>', attributes: {class: '<classname>'} }";
            this.$lastError += "\n\nSetting empty object to prevent editor get undefined";
            console.error(this.$lastError);

            obj = {
                name: '',
                element: '',
                attributes: {
                    class: ''
                }
            };
        }
        this.$config.stylesSet.push(obj);
    }

    _isValueInObject(obj, value) {
        for (let key in obj) {
            if (obj.hasOwnProperty(key) && obj[key] === value) {
                return true;
            }
        }
        return false;
    }

    _removeFromArray(elem, arr) {
        return arr.filter(function(e) {
            return e !== elem;
        });
    }

    _isInToolbar(elem, config) {
        for (let i = 0; i < config.toolbar.length; i++) {
            if (angular.isArray(config.toolbar[i])) {
                if (config.toolbar[i].indexOf(elem) !== -1) {
                    return true;
                }
            } else if (typeof config.toolbar[i] === "string" && config.toolbar[i] === elem) {
                return true;
            }
        }
        return false;
    }

    _getTaskInfoService() {
        if (!this._taskInfoService) {
            this._taskInfoService = this.$injector.get('TaskInfoService');
        }
        return this._taskInfoService;
    }
}

export default RichTextEditorService;